import type { FreeCreditsTask } from '@/app/types';

const TASK_REWARD_VALIDITY_MS = 30 * 24 * 60 * 60 * 1000;

const isExpired = (expiredAt: string | undefined, now: number): boolean => {
  if (!expiredAt) {
    return false;
  }
  const expiryTime = Date.parse(expiredAt);
  return !Number.isNaN(expiryTime) && expiryTime <= now;
};

/**
 * 浮标沿用新手奖励的有效期。新版任务过期后可能不再返回新手任务，因此用注册时间
 * 的 30 天窗口作为兜底，避免多步骤任务仍未完成时浮标永久保留。
 */
export const getTaskRewardExpiryTime = (
  tasks: Array<Pick<FreeCreditsTask, 'expired_at'>>,
  userCreatedAt?: string,
): number | undefined => {
  const taskExpiryTimes = tasks
    .map((task) => (task.expired_at ? Date.parse(task.expired_at) : Number.NaN))
    .filter((expiryTime) => !Number.isNaN(expiryTime));

  if (taskExpiryTimes.length > 0) {
    return Math.min(...taskExpiryTimes);
  }

  if (!userCreatedAt) {
    return undefined;
  }

  const createdTime = Date.parse(userCreatedAt);
  return Number.isNaN(createdTime) ? undefined : createdTime + TASK_REWARD_VALIDITY_MS;
};

/** 任务仍有待完成或待领取状态时，浮标和任务入口都应保留。 */
export const isTaskRewardOutstanding = (
  task: Pick<FreeCreditsTask, 'finished' | 'state' | 'expired_at'>,
  now = Date.now(),
): boolean => {
  if (task.state !== 'pending_claim' && (task.finished || task.state === 'finished' || task.state === 'expired')) {
    return false;
  }

  if (isExpired(task.expired_at, now)) {
    return false;
  }

  return true;
};

const getConfiguredTaskReward = (
  task: Pick<FreeCreditsTask, 'credits_award' | 'total_times' | 'max_reward'>,
): number => {
  if (task.max_reward && task.max_reward > 0) {
    return task.max_reward;
  }

  const eachReward = task.credits_award ?? 0;
  const totalTimes = task.total_times && task.total_times > 0 ? task.total_times : 1;
  return eachReward * totalTimes;
};

export const getRemainingTaskReward = (task: FreeCreditsTask): number => {
  if (isExpired(task.expired_at, Date.now())) {
    return 0;
  }

  const eachReward = task.credits_award ?? 0;
  const totalReward = getConfiguredTaskReward(task);

  // pending_claim 表示任务已完成但父任务奖励尚未到账，不能再扣除 finished_times。
  if (task.state === 'pending_claim') {
    return totalReward;
  }

  if (task.finished || task.state === 'finished' || task.state === 'expired') {
    return 0;
  }

  const finishedTimes = Math.max(task.finished_times ?? 0, 0);

  return Math.max(totalReward - eachReward * finishedTimes, 0);
};

/** 进阶父任务的 credits_award 是整张卡的奖励，不按 total_times 重复计算。 */
export const getRemainingParentTaskReward = (task: FreeCreditsTask): number =>
  isTaskRewardOutstanding(task) ? getConfiguredTaskReward({ ...task, total_times: 1 }) : 0;

export const getTaskCenterTotalReward = (
  newbieTasks: FreeCreditsTask[],
  advancedCreationTasks: FreeCreditsTask[],
  advertisementTasks: FreeCreditsTask[],
): number =>
  newbieTasks.reduce((sum, task) => sum + getRemainingTaskReward(task), 0) +
  advancedCreationTasks.reduce((sum, task) => sum + getRemainingParentTaskReward(task), 0) +
  advertisementTasks.reduce((sum, task) => sum + getRemainingTaskReward(task), 0);
